import { useCreateHeliosDataTablesRecordSilentMutation, useUpdateHeliosDataTablesRecordSilentMutation } from 'store/services/helios/dataHelios';
import { type MutableRefObject, useCallback } from 'react';
import type { FormDataRows } from '../helios/row.ts';
import { isNewRow, prepareRowData } from '../helios/row.ts';
import type { TableFormData, TableRef } from 'components/common/Table/Table.tsx';
import type { CreateHeliosDataTableRecordReq, CreateHeliosDataTableRecordRes } from 'types/dataHelios.ts';
import type { FailedRowUpdate, SaveAllRowsResult, SaveRowData, UpdateRowResult } from 'types/helios/record.ts';
import { isNil } from 'lodash';
import type { HeliosRowData } from './userAgendaTable/useHeliosAgendaTableActions.ts';

import { getRowId } from 'components/common/Table/tableFunctions.tsx';

export const useHeliosSaveRecord = () => {
  const [createHeliosDataTablesRecord] = useCreateHeliosDataTablesRecordSilentMutation();
  const [updateHeliosDataTablesRecord] = useUpdateHeliosDataTablesRecordSilentMutation();

  const createNewRecord = useCallback(
    async (rowData: HeliosRowData, data: SaveRowData): Promise<CreateHeliosDataTableRecordRes> => {
      const createData: CreateHeliosDataTableRecordReq = { ...rowData };
      const { masterRecordId } = data;

      if (!isNil(masterRecordId)) {
        createData.masterRecordNumber = masterRecordId;
      }

      return await createHeliosDataTablesRecord(createData).unwrap();
    },
    [createHeliosDataTablesRecord]
  );

  const updateExistingRecord = useCallback(
    async (rowData: HeliosRowData, rowId: string, data: SaveRowData): Promise<CreateHeliosDataTableRecordRes> => {
      const updateData: CreateHeliosDataTableRecordReq = { ...rowData };
      const { masterRecordId } = data;

      updateData.recordNumber = Number(getRowId(rowId));

      if (!isNil(masterRecordId)) {
        updateData.masterRecordNumber = masterRecordId;
      }

      return await updateHeliosDataTablesRecord(updateData).unwrap();
    },
    [updateHeliosDataTablesRecord]
  );

  const saveSingleRow = useCallback(
    async (
      rowId: string,
      rowData: HeliosRowData,
      formData: Record<string, Record<string, unknown>>,
      data: SaveRowData,
      tableRef?: MutableRefObject<TableRef | null>
    ): Promise<UpdateRowResult> => {
      let res: CreateHeliosDataTableRecordRes;

      if (isNewRow(rowId)) {
        res = await createNewRecord(rowData, data);
      } else {
        res = await updateExistingRecord(rowData, rowId, data);
      }

      tableRef?.current?.resetField(rowId, formData[rowId]);

      return { rowId, success: true, templateId: rowData.templateId, submittedColumns: rowData.columns, data: res };
    },
    [createNewRecord, updateExistingRecord]
  );

  const saveAllRows = useCallback(
    async (
      rows: FormDataRows,
      formData: TableFormData,
      data: SaveRowData,
      tableRef?: MutableRefObject<TableRef | null>
    ): Promise<SaveAllRowsResult> => {
      const rowPayloadMap = new Map<string, HeliosRowData>();

      const savePromises = Object.entries(rows).map(([rowId, row]) => {
        const rowData = prepareRowData(row, data);
        rowPayloadMap.set(rowId, rowData);
        return saveSingleRow(rowId, rowData, formData, data, tableRef);
      });
      const results = await Promise.allSettled(savePromises);

      const successfulUpdates: UpdateRowResult[] = [];
      const failedUpdates: FailedRowUpdate[] = [];

      results.forEach((result, index) => {
        const rowId = Object.keys(rows)[index];
        const rowData = rowPayloadMap.get(rowId);

        if (result.status === 'fulfilled') {
          successfulUpdates.push(result.value);
        } else {
          failedUpdates.push({
            rowId: getRowId(rowId),
            templateId: rowData?.templateId ?? data.templateId,
            error: result.reason,
          });
        }
      });

      return { successfulUpdates, failedUpdates };
    },
    [saveSingleRow]
  );

  return { saveAllRows, saveSingleRow };
};
